import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PortableText } from '@portabletext/react';
import {
  getIslandAreaMapPoints,
  getIslandCapabilities,
  getIslandConfig,
  getIslandFromHostname,
} from 'alakai-aloha-shared';
import { RegionDetailView } from 'alakai-aloha-shared/public-ui';
import { fetchRegionBySlug, fetchAreasByRegion } from '../../lib/sanityHelpers';
import { buildRuntimeCanonicalUrl, getRuntimePublicBaseUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { trackEvent } from '../../utils/analytics';
import { normalizeActivityDocument } from '../../lib/activityTaxonomy';

const portableTextComponents = {
  types: {
    image: ({ value }) => (
      <img
        src={value.asset?.url}
        alt={value.alt || 'Region image'}
        className="my-4 rounded"
      />
    ),
  },
  marks: {
    link: ({ value, children }) => (
      <a
        href={value.href}
        className="underline hover"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl font-extrabold tracking-tight mt-12 mb-6">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl font-bold mt-10 mb-4 border-b pb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl font-semibold mt-6 mb-2">{children}</h3>,
    h4: ({ children }) => <h4 className="text-lg font-medium italic mt-4 mb-1">{children}</h4>,
    normal: ({ children }) => <p className="mb-4">{children}</p>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
};

function mapExperience(experience) {
  const activity = normalizeActivityDocument(experience);
  return {
    id: activity._id,
    name: activity.name,
    slug: activity.slug,
    area: activity.area,
    category: activity.displayCategory || activity.category,
    cost: activity.cost,
    shortDescription: activity.shortDescription,
    imageUrl: activity.images?.[0]?.asset?.url,
    imageAlt: activity.images?.[0]?.alt || activity.name,
  };
}

const RegionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const siteContext = React.useMemo(() => getRuntimeSiteContext(), []);
  const runtimeIsland = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      return getIslandFromHostname(window.location.hostname) || siteContext.island;
    }

    return siteContext.island;
  }, [siteContext.island]);
  const islandConfig = React.useMemo(() => getIslandConfig(runtimeIsland), [runtimeIsland]);
  const capabilities = React.useMemo(() => getIslandCapabilities(runtimeIsland), [runtimeIsland]);
  const areaMapPoints = React.useMemo(() => getIslandAreaMapPoints(runtimeIsland), [runtimeIsland]);
  const [regionData, setRegionData] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRegionData = async () => {
      setLoading(true);

      try {
        const sanityData = await fetchRegionBySlug(slug);
        if (!sanityData) {
          setError(`Region "${slug}" not found in Sanity CMS.`);
          return;
        }

        setRegionData(sanityData);
        const regionAreas = await fetchAreasByRegion(slug);
        setAreas(regionAreas || []);
      } catch (fetchError) {
        console.error('Error fetching region data:', fetchError);
        setError(`Failed to load region data: ${fetchError.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchRegionData();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading region details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-2xl mx-auto text-center p-8">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Sanity Data Required</h1>
          <p className="text-lg mb-4">{error}</p>
        </div>
      </div>
    );
  }

  if (!regionData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Region not found</div>
      </div>
    );
  }

  const regionTitle = regionData.title || regionData.displayName;
  const pageTitle = regionData.seo?.title || `${regionTitle} | ${islandConfig.shortTitle} Travel Guide | Alakai Aloha`;
  const pageDescription =
    regionData.seo?.description ||
    regionData.shortDescription ||
    `Discover ${regionTitle} on ${islandConfig.title}. ${regionData.oneLiner}`;
  const siteUrl = getRuntimePublicBaseUrl();
  const pageUrl = buildRuntimeCanonicalUrl(`/regions/${slug}`);
  const pageImage = regionData.heroImage?.asset?.url || null;
  const placeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: regionTitle,
    description: pageDescription,
    url: pageUrl,
    ...(pageImage ? { image: pageImage } : {}),
    touristType: regionData.bestFor || [],
    containedInPlace: {
      '@type': 'Place',
      name: islandConfig.title,
    },
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Regions',
        item: `${siteUrl}/regions`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: regionTitle,
        item: pageUrl,
      },
    ],
  };

  return (
    <div className="relative z-20">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="Alakai Aloha" />
        {pageImage && <meta property="og:image" content={pageImage} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}

        <script type="application/ld+json">{JSON.stringify(placeJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <RegionDetailView
        islandKey={runtimeIsland}
        region={{
          title: regionTitle,
          slug,
          oneLiner: regionData.oneLiner,
          shortDescription: regionData.shortDescription,
          bestFor: regionData.bestFor || [],
          tradeoffs: regionData.tradeoffs || [],
          logisticsNotes: regionData.logisticsNotes,
          heroImageUrl: regionData.heroImage?.asset?.url,
          heroImageAlt: regionData.heroImage?.alt,
          articleContent: regionData.reviewArticle
            ? <PortableText value={regionData.reviewArticle} components={portableTextComponents} />
            : null,
          areas: areas.map((area) => ({
            id: area._id,
            title: area.title,
            slug: area.slug?.current || area.slug,
            oneLiner: area.oneLiner,
            heroImageUrl: area.heroImage?.asset?.url,
            heroImageAlt: area.heroImage?.alt,
          })),
          signatureExperiences: (regionData.signatureExperiences || []).map(mapExperience),
        }}
        areaMapPoints={areaMapPoints}
        mapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
        showCatalogLinks={capabilities.profileCatalogs}
        showPlannerPromo={capabilities.tripPlanner || capabilities.stayQuiz}
        onMapAreaSelect={(areaSlug) => navigate(`/areas/${areaSlug}`)}
        onQuizClick={() => {
          trackEvent('quiz_cta_click', { source: 'region_profile_promo_hero', cta: 'primary' });
        }}
        onInterviewClick={() => {
          trackEvent('interview_cta_click', { source: 'region_profile_promo_hero', cta: 'secondary' });
        }}
      />
    </div>
  );
};

export default RegionDetail;
